import React from 'react';

import { SidePage, Section } from '@gogame/components';

import Actions from 'scenes/Gallery/GalleryPage/Concepts/Actions';
import {
  ConceptReviewsList,
  useReviewsCount,
} from 'scenes/Gallery/GalleryPage/Concepts/Reviews';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';
import {
  AttributeList,
  AttributeItem,
} from 'scenes/Gallery/components/AttributeList';
import { DetailsFile } from 'scenes/Gallery/components/File';
import {
  URL,
  ROOT_HTML_URL,
  EXTENSION,
  WIDTH,
  HEIGHT,
  VIDEO_LENGTH,
} from 'scenes/Gallery/components/File/constants';
import TrackerLink from 'scenes/Gallery/components/TrackerLink';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { DIMENSION_CONCEPT } from 'scenes/Trackers/Tracker/constants';

import ConceptDetailsGeneral from './ConceptDetailsGeneral';
import ConceptDetailsInfo from './ConceptDetailsInfo';

const ConceptDetails = ({ data, onRemove, openChildComponent }) => {
  const { isAllowPerformance, isAllowExclusiveForAffiliates } =
    useGalleryPermissions();

  const {
    [CONCEPT.ID]: conceptId,
    [CONCEPT.HAS_STATS]: hasStats,
    [CONCEPT.CREATIVES_COUNT]: creativesCount,
    [CONCEPT.STATE_SCHEDULES]: stateSchedules,
    [CONCEPT.ORIGINAL]: {
      [CREATIVE.FILE]: {
        [URL]: originalFileUrl,
        [ROOT_HTML_URL]: originalFileRootHtmlUrl,
        [EXTENSION]: originalFileExtension,
        [WIDTH]: originalFileWidth,
        [HEIGHT]: originalFileHeight,
        [VIDEO_LENGTH]: originalFileVideoLength,
      },
      [CREATIVE.CREATIVE_TYPE]: originalCreativeTypeId,
      [CREATIVE.AI_GENERATED]: originalAIGenerated,
    },
    [CONCEPT.EXCLUSIVE_FOR_AFFILIATES]: exclusiveForAffiliates,
  } = data;

  const reviewsCount = useReviewsCount(conceptId);

  return (
    <SidePage.NewBody wrapper={true}>
      <SidePage.NewContent withSidebar={true}>
        <DetailsFile
          src={originalFileRootHtmlUrl || originalFileUrl}
          width={originalFileWidth}
          height={originalFileHeight}
          maxWidth={688}
          maxHeight={400}
          creativeTypeId={originalCreativeTypeId}
          attributes={
            <AttributeList type={'text'} listSeparator={'|'}>
              <AttributeList type={'text'} listSeparator={'/'}>
                <AttributeItem
                  valueParser={'creativeType'}
                  value={originalCreativeTypeId}
                />
                <AttributeItem
                  valueParser={'fileExtension'}
                  value={originalFileExtension}
                />
              </AttributeList>
              {originalFileVideoLength ? (
                <AttributeItem
                  valueParser={'fileVideoLength'}
                  value={originalFileVideoLength}
                />
              ) : null}
              {creativesCount ? (
                <AttributeItem
                  valueParser={'variantsCount'}
                  value={creativesCount}
                />
              ) : null}
            </AttributeList>
          }
        />
        <Section
          title={'General'}
          action={
            <AttributeList variant={'outlined'} typographyVariant={'caption'}>
              {exclusiveForAffiliates.length &&
              isAllowExclusiveForAffiliates ? (
                <AttributeItem valueParser={'exclusiveForAffiliates'} />
              ) : null}
              {originalAIGenerated ? (
                <AttributeItem valueParser={'aiGenerated'} />
              ) : null}
              {hasStats && isAllowPerformance ? (
                <TrackerLink dimension={DIMENSION_CONCEPT} id={conceptId}>
                  <AttributeItem
                    valueParser={'ads'}
                    clickable={true}
                    underline={'always'}
                  />
                </TrackerLink>
              ) : null}
            </AttributeList>
          }
        >
          <ConceptDetailsGeneral
            data={data}
            openChildComponent={openChildComponent}
          />
        </Section>
        <Section title={`Reviews${reviewsCount ? ` (${reviewsCount})` : ''}`}>
          <ConceptReviewsList conceptId={conceptId} disableGutters={true} />
        </Section>
      </SidePage.NewContent>
      <SidePage.Sidebar
        actions={
          <Actions
            actions={data?.actions}
            data={data}
            stateSchedules={stateSchedules}
            onRemove={onRemove}
            onOpenChildComponent={openChildComponent}
            withAddComment={false}
          />
        }
      >
        <ConceptDetailsInfo
          data={data}
          variant={'column'}
          openChildComponent={openChildComponent}
        />
      </SidePage.Sidebar>
    </SidePage.NewBody>
  );
};

export default ConceptDetails;
